import React, { useState, useEffect } from 'react';
import { sollecitiApi, pagamentiApi, contrattiApi, usersApi, immobiliApi } from '../../lib/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Badge } from '../../components/ui/badge';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { 
  Plus, 
  AlertTriangle,
  Send,
  CheckCircle,
  User,
  Euro,
  Calendar,
  Home,
  Search,
  Archive,
  FileText,
  RefreshCw
} from 'lucide-react';
import { toast } from 'sonner';

const SollecitiPage = () => {
  const [solleciti, setSolleciti] = useState([]);
  const [pagamentiAperti, setPagamentiAperti] = useState([]);
  const [contratti, setContratti] = useState([]);
  const [users, setUsers] = useState([]);
  const [immobili, setImmobili] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStato, setFilterStato] = useState('attivi'); // attivi, risolti, tutti
  const [filterContratto, setFilterContratto] = useState('all');
  const [formData, setFormData] = useState({
    pagamento_id: '',
    messaggio: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [sollRes, pagRes, contRes, usersRes, immRes] = await Promise.all([
        sollecitiApi.getAll(),
        pagamentiApi.getAll(), // Prendi tutti i pagamenti, poi filtriamo
        contrattiApi.getAll(),
        usersApi.getAll(),
        immobiliApi.getAll()
      ]);
      setSolleciti(sollRes.data);
      
      // "Da sollecitare" = pagamenti SDD in stato "pending" da più di 2 giorni 
      // dalla data di incasso prevista, che NON hanno già un sollecito attivo
      const sollecitatiIds = sollRes.data
        .filter(s => s.stato !== 'risolto')
        .map(s => s.pagamento_id);
      
      const oggi = new Date();
      const dueGiorniFa = new Date(oggi);
      dueGiorniFa.setDate(dueGiorniFa.getDate() - 2);
      
      const pagamentiDaSollecitare = pagRes.data.filter(p => {
        // Solo SDD
        if (p.tipo?.toLowerCase() !== 'sdd') return false;
        // Non già sollecitati
        if (sollecitatiIds.includes(p.id)) return false;
        // Solo "pending" (dopo export XML con data incasso)
        if (p.status !== 'pending') return false;
        // Data incasso passata da più di 2 giorni
        const dataIncasso = p.data_incasso_prevista ? new Date(p.data_incasso_prevista) : null;
        if (!dataIncasso) return false;
        return dataIncasso < dueGiorniFa;
      });
      
      setPagamentiAperti(pagamentiDaSollecitare);
      setContratti(contRes.data);
      setUsers(usersRes.data);
      setImmobili(immRes.data);
    } catch (error) {
      console.error('Errore caricamento:', error);
      toast.error('Errore nel caricamento dati');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await sollecitiApi.create({
        pagamento_id: formData.pagamento_id,
        messaggio: formData.messaggio
      });
      toast.success('Sollecito creato');
      setShowDialog(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante la creazione');
    }
  };

  const handleUpdateStato = async (id, stato) => {
    try {
      await sollecitiApi.updateStato(id, stato);
      toast.success(`Stato aggiornato a "${stato}"`);
      fetchData();
    } catch (error) {
      toast.error('Errore durante l\'aggiornamento');
    }
  };

  // Reinvia sollecito - crea un nuovo sollecito per lo stesso pagamento
  const handleReinvia = async (sollecito) => {
    try {
      await sollecitiApi.create({
        pagamento_id: sollecito.pagamento_id,
        messaggio: `[REINVIO] ${sollecito.messaggio || 'Sollecito pagamento'}`
      });
      toast.success('Nuovo sollecito inviato');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore durante il reinvio');
    }
  };

  const resetForm = () => {
    setFormData({
      pagamento_id: '',
      messaggio: ''
    });
  };

  const getContrattoInfo = (contrattoId) => {
    const cont = contratti.find(c => c.id === contrattoId);
    if (!cont) return { inquilino: '-', immobile: '-', codice: '-' };
    
    const inquiliniIds = cont.inquilini_ids || (cont.inquilino_id ? [cont.inquilino_id] : []);
    const inq = users.find(u => inquiliniIds.includes(u.id));
    const imm = immobili.find(i => i.id === cont.property_id);
    
    return {
      inquilino: inq ? `${inq.nome} ${inq.cognome}` : '-',
      inquilinoEmail: inq?.email || '-',
      immobile: imm ? `${imm.indirizzo}, ${imm.citta}` : '-',
      codice: cont.codice_contratto || '-'
    };
  };

  const getInquilinoInfo = (inquilinoId) => {
    const inq = users.find(u => u.id === inquilinoId);
    return inq ? { nome: `${inq.nome} ${inq.cognome}`, email: inq.email } : { nome: '-', email: '-' };
  };

  const getStatoBadge = (stato) => {
    switch (stato) {
      case 'inviato': return { class: 'bg-amber-100 text-amber-700 border-amber-300', icon: Send, label: 'Inviato' };
      case 'risolto': return { class: 'bg-green-100 text-green-700 border-green-300', icon: CheckCircle, label: 'Archiviato' };
      default: return { class: 'bg-amber-100 text-amber-700 border-amber-300', icon: Send, label: 'Inviato' };
    }
  };

  // Calcola importo SDD dai components
  const calcolaImportoSDD = (payment) => {
    // Se c'è già importo_sdd, usalo
    if (payment.importo_sdd) return payment.importo_sdd;
    if (payment.amount) return payment.amount;
    
    // Altrimenti calcola dai components
    const comp = payment.components || {};
    const affitto = comp.affitto_mensile || 0;
    const condominio = comp.quota_condominio || 0;
    const bollo = comp.imposta_bollo || 0;
    const saConduttore = comp.sa_9_conduttore || 0;
    const scontoSaInq = comp.sconto_sa_inq || 0;
    
    // SDD = Affitto + Condominio + Bollo + SA_Conduttore - Sconto
    return affitto + condominio + bollo + saConduttore - scontoSaInq;
  };

  const generateDefaultMessage = (pagamentoId) => {
    const pag = pagamentiAperti.find(p => p.id === pagamentoId);
    if (!pag) return '';
    
    const info = getContrattoInfo(pag.contract_id);
    const importo = calcolaImportoSDD(pag);
    return `Gentile ${info.inquilino},

La informiamo che risulta ancora non pagato l'importo di € ${importo.toLocaleString('it-IT', { minimumFractionDigits: 2 })} con scadenza ${new Date(pag.due_date || pag.month).toLocaleDateString('it-IT')} relativo a: ${pag.description || 'Canone mensile'}.

La preghiamo di provvedere al pagamento al più presto.

Cordiali saluti,
SemplificAffitto`;
  };

  // Filtro solleciti
  const filteredSolleciti = solleciti.filter(soll => {
    // Filtro stato
    if (filterStato === 'attivi' && soll.stato === 'risolto') return false;
    if (filterStato === 'risolti' && soll.stato !== 'risolto') return false;
    
    // Filtro contratto
    if (filterContratto !== 'all' && soll.contratto_id !== filterContratto) return false;
    
    // Filtro ricerca
    if (searchTerm) {
      const info = getInquilinoInfo(soll.inquilino_id);
      const searchLower = searchTerm.toLowerCase();
      if (!info.nome.toLowerCase().includes(searchLower) &&
          !soll.messaggio?.toLowerCase().includes(searchLower)) {
        return false;
      }
    }
    
    return true;
  });

  // Stats
  const stats = {
    inviati: solleciti.filter(s => s.stato !== 'risolto').length,
    risolti: solleciti.filter(s => s.stato === 'risolto').length
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="loading-spinner" />
      </div>
    );
  }

  return (
    <div data-testid="solleciti-page" className="p-6">
      <div className="page-header mb-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Solleciti Pagamenti</h1>
            <p className="text-slate-500 mt-1">Gestisci i solleciti per pagamenti SDD in attesa</p>
          </div>
          <Button 
            onClick={() => { resetForm(); setShowDialog(true); }}
            className="bg-slate-900 hover:bg-slate-800"
            disabled={pagamentiAperti.length === 0}
            data-testid="new-sollecito-btn"
          >
            <Plus className="w-4 h-4 mr-2" />
            Nuovo Sollecito
          </Button>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card className="bg-amber-50 border-amber-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-amber-100 rounded-lg flex items-center justify-center">
                <Send className="w-6 h-6 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-amber-600 font-medium">Solleciti Inviati</p>
                <p className="text-2xl font-bold text-amber-700">{stats.inviati}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-green-50 border-green-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                <Archive className="w-6 h-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-green-600 font-medium">Archiviati</p>
                <p className="text-2xl font-bold text-green-700">{stats.risolti}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="bg-red-50 border-red-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-red-100 rounded-lg flex items-center justify-center">
                <AlertTriangle className="w-6 h-6 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-red-600 font-medium">Da Sollecitare</p>
                <p className="text-2xl font-bold text-red-700">{pagamentiAperti.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex-1 min-w-[200px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Cerca inquilino o messaggio..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                  data-testid="search-solleciti"
                />
              </div>
            </div>
            <div className="w-[180px]">
              <Select value={filterStato} onValueChange={setFilterStato}>
                <SelectTrigger data-testid="filter-stato">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="attivi">Solo Attivi</SelectItem>
                  <SelectItem value="risolti">Solo Archiviati</SelectItem>
                  <SelectItem value="tutti">Tutti</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="w-[250px]">
              <Select value={filterContratto} onValueChange={setFilterContratto}>
                <SelectTrigger data-testid="filter-contratto">
                  <SelectValue placeholder="Filtra per contratto" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tutti i contratti</SelectItem>
                  {contratti.map(c => (
                    <SelectItem key={c.id} value={c.id}>
                      {c.codice_contratto || c.id.slice(0, 8)}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Solleciti Table */}
      <Card className="mb-6">
        <CardContent className="p-0">
          <div className="p-4 border-b border-slate-200 bg-slate-50">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-slate-600" />
              <h2 className="font-semibold text-slate-900">Solleciti ({filteredSolleciti.length})</h2>
            </div>
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Inquilino</TableHead>
                <TableHead>Contratto</TableHead>
                <TableHead>Importo</TableHead>
                <TableHead>Mese</TableHead>
                <TableHead>Data Sollecito</TableHead>
                <TableHead>Stato</TableHead>
                <TableHead className="text-right">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredSolleciti.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-8 text-slate-500">
                    {filterStato === 'attivi' ? 'Nessun sollecito attivo' : 'Nessun sollecito trovato'}
                  </TableCell>
                </TableRow>
              ) : (
                filteredSolleciti.map((soll) => {
                  const inqInfo = getInquilinoInfo(soll.inquilino_id);
                  const contInfo = getContrattoInfo(soll.contratto_id);
                  const statoBadge = getStatoBadge(soll.stato);
                  const Icon = statoBadge.icon;
                  return (
                    <TableRow key={soll.id} data-testid={`sollecito-row-${soll.id}`}>
                      <TableCell>
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 bg-red-100 rounded-full flex items-center justify-center">
                            <User className="w-5 h-5 text-red-600" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">{inqInfo.nome}</p>
                            <p className="text-xs text-slate-500">{inqInfo.email}</p>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <FileText className="w-4 h-4 text-slate-400" />
                          <span className="text-sm font-mono">{contInfo.codice}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 font-semibold text-red-600">
                          <Euro className="w-4 h-4" />
                          {(soll.importo || 0).toLocaleString('it-IT', { minimumFractionDigits: 2 })}
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className="text-sm text-slate-600">{soll.mese_riferimento || '-'}</span>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2 text-sm">
                          <Calendar className="w-4 h-4 text-slate-400" />
                          {new Date(soll.created_at).toLocaleDateString('it-IT')}
                        </div>
                      </TableCell>
                      <TableCell>
                        <Badge className={statoBadge.class}>
                          <Icon className="w-3 h-3 mr-1" />
                          {statoBadge.label}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-right">
                        {soll.stato !== 'risolto' && (
                          <div className="flex items-center justify-end gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => handleReinvia(soll)}
                              className="text-amber-700 border-amber-300 hover:bg-amber-50"
                              data-testid={`reinvia-${soll.id}`}
                            >
                              <RefreshCw className="w-4 h-4 mr-1" />
                              Reinvia
                            </Button>
                            <Button
                              size="sm"
                              onClick={() => handleUpdateStato(soll.id, 'risolto')}
                              className="bg-green-600 hover:bg-green-700"
                              data-testid={`mark-resolved-${soll.id}`}
                            >
                              <CheckCircle className="w-4 h-4 mr-1" />
                              Archivia
                            </Button>
                          </div>
                        )}
                        {soll.stato === 'risolto' && soll.risolto_at && (
                          <span className="text-xs text-slate-500">
                            {new Date(soll.risolto_at).toLocaleDateString('it-IT')}
                          </span>
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Pagamenti SDD Aperti da Sollecitare */}
      {pagamentiAperti.length > 0 && (
        <Card>
          <CardContent className="p-0">
            <div className="p-4 border-b border-slate-200 bg-red-50">
              <div className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-red-600" />
                <h2 className="font-semibold text-red-900">Pagamenti SDD da Sollecitare ({pagamentiAperti.length})</h2>
              </div>
              <p className="text-sm text-red-600 mt-1">Pagamenti SDD in attesa da più di 2 giorni dalla data di incasso prevista</p>
            </div>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Inquilino</TableHead>
                  <TableHead>Immobile</TableHead>
                  <TableHead>Descrizione</TableHead>
                  <TableHead>Mese</TableHead>
                  <TableHead>Importo SDD</TableHead>
                  <TableHead className="text-right">Azione</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pagamentiAperti.map((pag) => {
                  const info = getContrattoInfo(pag.contract_id);
                  return (
                    <TableRow key={pag.id} data-testid={`aperto-row-${pag.id}`}>
                      <TableCell>
                        <div>
                          <p className="font-medium">{info.inquilino}</p>
                          <p className="text-xs text-slate-500">{info.inquilinoEmail}</p>
                        </div>
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          <Home className="w-4 h-4 text-slate-400" />
                          <span className="text-sm">{info.immobile}</span>
                        </div>
                      </TableCell>
                      <TableCell>{pag.description || 'Canone mensile'}</TableCell>
                      <TableCell>{pag.month}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 font-semibold text-red-600">
                          <Euro className="w-4 h-4" />
                          {calcolaImportoSDD(pag).toLocaleString('it-IT', { minimumFractionDigits: 2 })}
                        </div>
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          size="sm"
                          variant="destructive"
                          onClick={() => {
                            setFormData({
                              pagamento_id: pag.id,
                              messaggio: generateDefaultMessage(pag.id)
                            });
                            setShowDialog(true);
                          }}
                          data-testid={`create-sollecito-${pag.id}`}
                        >
                          <AlertTriangle className="w-4 h-4 mr-2" />
                          Sollecita
                        </Button>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      )}

      {/* Dialog Nuovo Sollecito */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Nuovo Sollecito</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Pagamento SDD da Sollecitare *</Label>
              <Select 
                value={formData.pagamento_id} 
                onValueChange={(v) => setFormData({
                  ...formData, 
                  pagamento_id: v,
                  messaggio: formData.messaggio || generateDefaultMessage(v)
                })}
              >
                <SelectTrigger data-testid="select-pagamento-sollecito">
                  <SelectValue placeholder="Seleziona pagamento..." />
                </SelectTrigger>
                <SelectContent>
                  {pagamentiAperti.map(pag => {
                    const info = getContrattoInfo(pag.contract_id);
                    return (
                      <SelectItem key={pag.id} value={pag.id}>
                        {info.inquilino} - € {calcolaImportoSDD(pag).toLocaleString('it-IT')} ({pag.month})
                      </SelectItem>
                    );
                  })}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Messaggio Sollecito *</Label>
              <Textarea
                value={formData.messaggio}
                onChange={(e) => setFormData({...formData, messaggio: e.target.value})}
                placeholder="Scrivi il messaggio di sollecito..."
                rows={8}
                required
                data-testid="input-messaggio-sollecito"
              />
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                Annulla
              </Button>
              <Button 
                type="submit" 
                className="bg-red-600 hover:bg-red-700"
                disabled={!formData.pagamento_id || !formData.messaggio}
                data-testid="submit-sollecito"
              >
                <Send className="w-4 h-4 mr-2" />
                Crea Sollecito
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SollecitiPage;
